'use client'

import { useEffect, useState, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { useAuth } from '@/lib/auth-provider'
import { LandingHeader } from '@/components/landing/landing-header'
import { Hero } from '@/components/landing/hero'
import { Features } from '@/components/landing/features'
import { DetailedFeatures } from '@/components/landing/detailed-features'
import { Comparison } from '@/components/landing/comparison'
import { Pricing } from '@/components/landing/pricing'
import { LandingFooter } from '@/components/landing/landing-footer'
import { captureEvent } from '@/lib/posthog-helper'

export default function Home() {
  const { user, loading } = useAuth()
  const router = useRouter()
  const [isRedirecting, setIsRedirecting] = useState(false)
  const hasRedirectedRef = useRef(false)

  // Detect OAuth callback and redirect to /auth/callback
  useEffect(() => {
    if (typeof window === 'undefined') return
    
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const source = params.get('source')
    
    if (code && source === 'tauri' && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true
      setIsRedirecting(true)
      router.replace(`/auth/callback?code=${code}&source=tauri`)
      return
    }
  }, [router])

  // PostHog: Track landing page view
  useEffect(() => {
    if (typeof window === 'undefined') return
    const timer = setTimeout(() => {
      captureEvent('signup_flow:landing_page_view', {
        referrer: document.referrer,
        utm_source: new URLSearchParams(window.location.search).get('utm_source'),
        utm_medium: new URLSearchParams(window.location.search).get('utm_medium'),
        utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign'),
      })
    }, 500)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    // ⚡ Optimization: Redirect immediately when user is detected
    // Use ref to prevent multiple redirects
    if (user && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true
      setIsRedirecting(true)
      // Use replace instead of push for faster navigation
      router.replace('/grid')
    }
  }, [user, router])

  // Si l'utilisateur est connecté ou en cours de redirection
  if (user || isRedirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    )
  }

  // Afficher un écran de chargement pendant la vérification de l'authentification
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    )
  }

  // Si l'utilisateur n'est pas connecté, afficher la landing page
  return (
    <main className="min-h-screen">
      <LandingHeader />
      <Hero />
      <Features />
      <DetailedFeatures />
      <Comparison />
      <Pricing />
      <LandingFooter />
    </main>
  )
}
